import { Component, OnInit, inject, input, output, signal } from '@angular/core';
import { Marker } from '../../models/classes/marker';
import {
  IHtstSensorDetails,
  MacAddress,
  MatrixCode,
  SerialNumber,
} from '../../models/classes/sensor';
import { InstructionPopupItem } from '../../models/popup.model';
import { I18nService } from '../../services/i18n.service';
import { UiService } from '../../services/ui.service';
import { AssetsService } from '../../services/asset.service';
import { InstructionPopupComponent } from '../instruction-popup/instruction-popup';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { HtstColorCodeComponent } from '../htst-color-code/htst-color-code';
import { SettingsService } from '../../services/settings.service';

@Component({
  selector: 'app-htst-inlet-sensor',
  templateUrl: './htst-inlet-sensor.html',
  imports: [InstructionPopupComponent, NzIconModule, HtstColorCodeComponent],
  styleUrls: ['./htst-inlet-sensor.scss'],
})
export class HtstInletSensorComponent implements OnInit {
  hasCamera = input<boolean>(false);
  allowedInletDigits = input<any[]>([]);
  qrDigits = input<SerialNumber>(new SerialNumber('i'));
  qrDAU = input.required<MacAddress>();
  qrInletGateway = input.required<MatrixCode>();
  assetID = input.required<any>();
  settingService = inject(SettingsService);
  wizardsteps = input<string[]>([]);
  currentstep = input<number>(0);
  counter = input<number>(0);
  inletSerialNumberEvent = output<SerialNumber>();
  openScannerEvent = output<number>();
  inletSensorHtst = output<IHtstSensorDetails>();
  navBack = output<void>();
  navExit = output<void>();
  navContinue = output<void>();
  navHeader = output<number>();
  showQrScanPopup = signal(false);
  showGatewayErrorPopup = signal(false);
  showInletQrLocationPopup = signal(false);
  isFaultyQrCode = signal(false);
  saving = signal(false);

  colorData = signal<any>(null);
  selectedColor = signal<any>(null);
  gatewayErrInstruction = signal<InstructionPopupItem[]>([]);
  qrLocationInlet = signal<InstructionPopupItem[]>([]);
  inletModelNumbersOption = ['A-SE-23-0002'];
  inletModelNumberSelected = this.inletModelNumbersOption[0];
  ModelID = 'A-DU-5101L-0000';
  i18n = inject(I18nService);
  ui = inject(UiService);
  assetsService = inject(AssetsService);
  dauValue = this.settingService.commonDauInput;

  mappedCodeUpdate = signal<any>('');
  private allowedDigits: Record<string, any[]> = { i: [] };
  isMobile = this.ui.IsMobile;

  toggleGatewayErrorPopup(show: boolean) {
    this.showGatewayErrorPopup.set(show);
  }

  ngOnInit(): void {
    this.fetchSensorColorData();
    this.allowedDigits['i'] = this.allowedInletDigits();

    const currentQr = this.qrDigits();
    this.checkAllowedModelNumber(currentQr.modelNumber);

    if (!currentQr.modelNumber || currentQr.modelNumber.length === 0) {
      currentQr.modelNumber = this.inletModelNumbersOption[0];
    }

    this.checkDeviceDigits(currentQr);
  }

  fetchSensorColorData() {
    this.assetsService
      .getSensorColorData(
        this.qrDAU().value,
        this.ModelID,
        this.assetID(),
        this.settingService.inletGateWayValue.value
      )
      .then((data: any) => {
        const errorCode = data['ErrorCode']?.toLowerCase();
        if (errorCode === 'noavailablesensor') {
          this.showGatewayErrorPopup.set(true);
          this.gatewayErrInstruction.set([
            { image: 'assets/images/connection_error.png' },
            { title: this.i18n.langString('connection_capacity_popup_title') },
          ]);
        } else if (errorCode === 'gatewayerror') {
          this.showGatewayErrorPopup.set(true);
          this.gatewayErrInstruction.set([
            { image: 'assets/images/connection_error.png' },
            {
              title: this.i18n.langString('dau_with_exist_gateway') + data['Existing_GatewayID'],
            },
          ]);
        } else {
          this.showGatewayErrorPopup.set(false);
        }
        this.colorData.set(data['Sensors']);
      });
  }

  checkDeviceDigits(code: SerialNumber) {
    code.mappingValid = false;
    this.checkAllowedModelNumber(code.modelNumber);
    this.resetModelNumberToAllowedValue(code);
    if (code.validLength) {
      this.mappedCodeUpdate.set(code);

      if (
        this.allowedDigits[this.mappedCodeUpdate().device].some(
          (d) => d == this.mappedCodeUpdate().value
        )
      ) {
        if (this.mappedCodeUpdate().modelNumberValid) this.mappedCodeUpdate().mappingValid = true;
      } else {
        this.assetsService
          .checkDeviceMapping(this.mappedCodeUpdate().value, this.mappedCodeUpdate().device)
          .then((res) => {
            if (this.mappedCodeUpdate().modelNumberValid)
              this.mappedCodeUpdate().mappingValid = res.isValid;
          });
      }

      this.inletSerialNumberEvent.emit(code);
    }
  }

  checkAllowedModelNumber(modelNumber: string) {
    this.qrDigits().modelNumberValid = this.inletModelNumbersOption.includes(modelNumber);
  }

  resetModelNumberToAllowedValue(code: SerialNumber) {
    if (!code.modelNumberValid) {
      code.modelNumber = this.inletModelNumberSelected;
      this.isFaultyQrCode.set(true);
    }
  }

  getColor(color: any) {
    this.selectedColor.set(color);
    this.inletSensorHtst.emit(color);
  }

  toggleQrLocationInletPopup(show: boolean) {
    this.showInletQrLocationPopup.set(show);
    const mobileMarkers: any = [
      { title: this.i18n.langString('where_is_qr_code') },
      { text: this.i18n.langString('inlet_sensor_qr_instruction') },
      { text: this.i18n.langString('inlet_sensor_qr_instruction_step1') },
      {
        text: this.i18n.langString('inlet_sensor_qr_instruction_step2'),
        textbold: true,
      },
      {
        text: this.i18n.langString('inlet_sensor_qr_instruction_step3'),
        image: '...',
        marker: new Marker(0, '', 0.61, 0.35, 0.5),
      },
    ];

    this.qrLocationInlet.set(mobileMarkers);
  }
}
